import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { Groups } from '@/app/lib/auth/use-authorization.ts';
import { Authorization } from '@/app/lib/auth/authorization.tsx';

interface AuthorizedRouteProps {
  allowedGroups: Groups;
  children: ReactNode;
}

/**
 * Component that renders its children only if the user is authorized to view them.
 * If the user is not authorized, the user is redirected to the forbidden page.
 * @param allowedGroups Groups that are allowed to view the children.
 * @param children Children to render if the user is authorized.
 * @returns {ReactNode} Returns a React element which renders its children only if the user is authorized to view them.
 */
export function AuthorizedRoute({
  allowedGroups,
  children
}: Readonly<AuthorizedRouteProps>): ReactNode {
  return (
    <Authorization
      allowedGroups={allowedGroups}
      forbiddenFallback={<Navigate to={'/403'} />}
    >
      {children}
    </Authorization>
  );
}
